import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Flame, Scale, Activity, Droplet, Dumbbell, Target, CalendarCheck,
  TrendingDown, TrendingUp, Plus, ChevronRight, Percent, Quote, Pencil, CheckCircle2, Sparkles, Trophy,
} from "lucide-react";
import api from "@/lib/api";
import { localISODate } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { dailyQuote } from "@/lib/motivation";
import { StatCard, BmiBadge, PageLoader } from "@/components/Shared";
import { Sparkline, Ring } from "@/components/Charts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  const checkIn = useMutation({
    mutationFn: async () => (await api.post("/checkins", { date: localISODate() })).data,
    onSuccess: () => { toast.success(t("checkedIn")); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("goodMorning") : hour < 18 ? t("goodAfternoon") : t("goodEvening");
  const firstName = (user?.name || "").split(" ")[0];

  if (isLoading) return <DashSkeleton />;

  const d = data || {};
  const bfTrend = (d.bf_trend || []).map((x) => ({ value: x.value }));
  const waterPct = d.water_goal ? Math.round((d.water_ml / d.water_goal) * 100) : 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-sm text-muted-foreground">{greeting},</p>
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{firstName} 👋</h1>
        </div>
        <Button onClick={() => navigate("/train")} className="gap-2" data-testid="dash-start-workout">
          <Dumbbell className="h-4 w-4" /> {t("startWorkout")}
        </Button>
      </div>

      {/* Daily motivation */}
      <div className="flex items-start gap-4 rounded-2xl border bg-gradient-to-br from-primary/10 via-card to-card p-5" data-testid="daily-motivation">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary"><Quote className="h-5 w-5" /></span>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-primary">{t("dailyMotivation")}</div>
          <p className="mt-1 font-display text-lg font-semibold leading-snug">"{dailyQuote(lang)}"</p>
        </div>
      </div>

      {/* Check-in banner */}
      <div className="rounded-2xl border bg-gradient-to-br from-primary/10 to-transparent p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-[1_1_12rem] items-center gap-4">
            <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${d.checkin ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>
              <CalendarCheck className="h-6 w-6" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">{t("checkInStatus")}</div>
              <div className="font-display text-lg font-semibold">{d.checkin ? t("checkedIn") : t("notCheckedIn")}</div>
            </div>
          </div>
          {d.checkin ? (
            <div className="flex shrink-0 items-center gap-2 rounded-full bg-primary/15 px-3 py-1.5 text-sm font-semibold text-primary">
              <Flame className="h-4 w-4" /> {d.streak} {t("days")}
            </div>
          ) : (
            <Button onClick={() => navigate("/checkin")} data-testid="dash-checkin-btn" className="h-11 w-full gap-2 sm:h-9 sm:w-auto">
              {t("checkInNow")} <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <WeeklyTargetCard />

      <WeeklyRecapCard />

      {/* Bento grid */}
      <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
        <StatCard testid="stat-streak" icon={Flame} label={t("currentStreak")} value={d.streak ?? 0} unit={t("days")}
          accent="bg-orange-500/15 text-orange-500" sub={d.sessions_this_week ? `${d.sessions_this_week} ${t("sessionsWeek")}` : t("restDay")} />
        <StatCard testid="stat-weight" icon={Scale} label={t("latestWeight")} value={d.weight ?? "—"} unit="kg"
          sub={d.weight_delta != null && (
            <span className={`inline-flex items-center gap-1 ${d.weight_delta <= 0 ? "text-primary" : "text-warning"}`}>
              {d.weight_delta <= 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
              {Math.abs(d.weight_delta)} kg
            </span>
          )} />
        <StatCard testid="stat-bmi" icon={Activity} label={t("bmiTitle")} value={d.bmi ?? "—"}
          accent="bg-sky-500/15 text-sky-500">
          <div className="mt-2"><BmiBadge category={d.bmi_category} t={t} /></div>
        </StatCard>
        <StatCard testid="stat-bf" icon={Percent} label={t("bodyFat")} value={d.body_fat ?? "—"} unit={d.body_fat ? "%" : ""}>
          {bfTrend.length > 1 && <div className="mt-2"><Sparkline data={bfTrend} height={36} /></div>}
        </StatCard>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Water */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">{t("waterIntake")}</span>
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-sky-500/15 text-sky-500"><Droplet className="h-4 w-4" /></span>
          </div>
          <div className="mt-4 flex items-center gap-5">
            <Ring value={d.water_ml || 0} max={d.water_goal || 2500} label={`${waterPct}%`} color="hsl(199 89% 55%)" />
            <div>
              <div className="font-display text-2xl font-bold tabular-nums">{((d.water_ml || 0) / 1000).toFixed(1)}L</div>
              <div className="text-xs text-muted-foreground">/ {((d.water_goal || 2500) / 1000).toFixed(1)}L</div>
              <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => addWater(qc, 250, t)} data-testid="dash-add-water">
                <Plus className="h-3 w-3" /> 250ml
              </Button>
            </div>
          </div>
        </div>

        {/* Weekly volume */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">{t("weeklyVolume")}</span>
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary"><Dumbbell className="h-4 w-4" /></span>
          </div>
          <div className="mt-4 font-display text-3xl font-bold tabular-nums">{(d.weekly_volume || 0).toLocaleString()}<span className="text-base font-normal text-muted-foreground"> kg</span></div>
          <div className="mt-1 text-xs text-muted-foreground">{d.weekly_sets || 0} {t("set")} · {d.sessions_this_week || 0} {lang === "id" ? "sesi" : "sessions"}</div>
        </div>

        {/* Next goal */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">{t("nextGoal")}</span>
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary"><Target className="h-4 w-4" /></span>
          </div>
          {d.milestone ? (
            <>
              <div className="mt-4 font-display text-2xl font-bold tabular-nums">{d.milestone.remaining} {d.milestone.unit}</div>
              <div className="mt-1 text-xs text-muted-foreground">{lang === "id" ? "menuju" : "to"} {d.milestone.label}</div>
            </>
          ) : (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">{lang === "id" ? "Belum ada target aktif." : "No active goal yet."}</p>
              <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => navigate("/progress")} data-testid="dash-add-goal">
                <Plus className="h-3 w-3" /> {t("addGoal")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">{t("quickActions")}</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: CalendarCheck, label: t("checkIn"), to: "/checkin", tid: "qa-checkin" },
            { icon: Dumbbell, label: t("logWorkout"), to: "/train", tid: "qa-workout" },
            { icon: Scale, label: t("logBody"), to: "/body", tid: "qa-body" },
            { icon: Activity, label: t("progress"), to: "/progress", tid: "qa-progress" },
          ].map((a) => (
            <button key={a.to} onClick={() => navigate(a.to)} data-testid={a.tid}
              className="flex flex-col items-center gap-2 rounded-2xl border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-accent-foreground"><a.icon className="h-5 w-5" /></span>
              <span className="text-sm font-medium">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

async function addWater(qc, ml, t) {  await api.post("/water", { ml });
  toast.success(`+${ml}ml`);
  qc.invalidateQueries({ queryKey: ["dashboard"] });
}

function WeeklyRecapCard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const id = lang === "id";
  const { data } = useQuery({ queryKey: ["weekly-review"], queryFn: async () => (await api.get("/weekly-review")).data });
  const d = data || {};
  const hasActivity = (d.sessions || 0) > 0;

  const narrative = hasActivity
    ? (id
        ? `Minggu ini kamu latihan ${d.sessions}x, mengangkat ${(d.volume || 0).toLocaleString("id-ID")} kg total${d.calories ? `, dan membakar ~${d.calories.toLocaleString("id-ID")} kkal` : ""}. Pertahankan! 🔥`
        : `This week you trained ${d.sessions}x, lifted ${(d.volume || 0).toLocaleString()} kg total${d.calories ? `, and burned ~${d.calories.toLocaleString()} kcal` : ""}. Keep it up! 🔥`)
    : t("weeklyRecapEmpty");

  const chips = [
    { icon: Dumbbell, value: d.sessions ?? 0, label: id ? "Latihan" : "Sessions", cls: "text-primary" },
    { icon: Flame, value: (d.calories ?? 0).toLocaleString(), label: t("caloriesBurned"), cls: "text-orange-500" },
    { icon: Trophy, value: d.prs?.length ?? 0, label: "PR", cls: "text-amber-500" },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-card to-card p-5" data-testid="weekly-recap-card">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary"><Sparkles className="h-5 w-5" /></span>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-primary">{t("weeklyRecap")}</div>
          <p className="mt-0.5 text-sm font-medium leading-snug text-foreground">{narrative}</p>
        </div>
      </div>
      {hasActivity && (
        <div className="mt-4 grid grid-cols-3 gap-3">
          {chips.map((c, i) => (
            <div key={i} className="rounded-xl border bg-background/50 p-3 text-center" data-testid={`recap-chip-${i}`}>
              <c.icon className={cn("mx-auto h-4 w-4", c.cls)} />
              <div className="mt-1.5 font-display text-lg font-bold tabular-nums">{c.value}</div>
              <div className="text-[11px] text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      )}
      {!hasActivity && (
        <Button size="sm" className="mt-4 gap-2" onClick={() => navigate("/train")} data-testid="recap-start-btn">
          <Dumbbell className="h-4 w-4" /> {t("startWorkout")}
        </Button>
      )}
    </div>
  );
}

function WeeklyTargetCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["weekly-target"], queryFn: async () => (await api.get("/weekly-target")).data });

  const target = data?.target ?? 3;
  const completed = data?.completed ?? 0;
  const pct = Math.min(100, Math.round((completed / target) * 100));
  const reached = completed >= target;

  const save = async () => {
    const n = parseInt(val, 10);
    if (!n || n < 1) return;
    setSaving(true);
    try {
      const { data: res } = await api.put("/weekly-target", { target: n });
      qc.setQueryData(["weekly-target"], res);
      setOpen(false);
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border bg-gradient-to-br from-primary/10 to-transparent p-5" data-testid="weekly-target-card">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Target className="h-5 w-5" /></span>
          <div>
            <div className="font-display font-semibold">{t("weeklyTarget")}</div>
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground tabular-nums">{completed}/{target}</span> {t("workoutsThisWeek")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {reached && <span className="hidden rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary sm:inline-flex sm:items-center sm:gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {t("targetReached")}</span>}
          <button onClick={() => { setVal(String(target)); setOpen(true); }} data-testid="edit-target-btn"
            className="grid h-8 w-8 place-items-center rounded-lg border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label={t("editTarget")}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full transition-all duration-500", reached ? "bg-primary" : "bg-primary/70")} style={{ width: `${pct}%` }} data-testid="weekly-target-bar" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xs" data-testid="target-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-primary" /> {t("weeklyTarget")}</DialogTitle></DialogHeader>
          <div className="flex items-center gap-3">
            <Input type="number" min="1" max="14" inputMode="numeric" value={val} onChange={(e) => setVal(e.target.value)} className="tabular-nums text-center text-lg" data-testid="target-input" autoFocus />
            <span className="shrink-0 text-sm text-muted-foreground">{t("timesPerWeek")}</span>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="target-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DashSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-12 w-56" />
      <Skeleton className="h-20 w-full rounded-2xl" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
      </div>
    </div>
  );
}
